import React, { useEffect, useState } from 'react';
import { Leaf, ShoppingBag, Calendar } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

interface StateFeesAnalyticsProps {
  storeId: number;
  dateFrom: string;
  dateTo: string;
}

interface FeeTotal {
  category: number;
  label: string;
  units: number;
  gross_total: number;
  net_total: number;
}

interface FeeDaily {
  day: string;
  category: number;
  label: string;
  units: number;
  gross_total: number;
}

interface StateFeesData {
  range: { from: string; to: string };
  totals: FeeTotal[];
  daily: FeeDaily[];
}

const formatEuro = (n: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(n || 0);

const formatDate = (s: string) =>
  new Intl.DateTimeFormat('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .format(new Date(s));

// Icon + color per fee category
const CATEGORY_STYLE: Record<number, { color: string; bg: string; border: string }> = {
  8: { color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  16: { color: 'text-cyan-700 dark:text-cyan-300', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
};

const StateFeesAnalytics: React.FC<StateFeesAnalyticsProps> = ({ storeId, dateFrom, dateTo }) => {
  const [data, setData] = useState<StateFeesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/analytics/stores/${storeId}/state-fees?from=${dateFrom}&to=${dateTo}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } }
        );
        if (r.ok && !cancelled) setData(await r.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [storeId, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="bg-surface-2 dark:bg-surface-1/50 border border-line rounded-2xl p-6 animate-pulse">
        <div className="h-4 w-40 bg-surface-2 rounded mb-4" />
        <div className="h-24 bg-surface-2 dark:bg-surface-2/50 rounded" />
      </div>
    );
  }

  if (!data || data.totals.length === 0) {
    return (
      <div className="bg-surface-2 dark:bg-surface-1/50 border border-line rounded-2xl p-6 text-t3 text-sm">
        <div className="flex items-center gap-2 mb-2 text-t1">
          <Leaf className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <h3 className="font-semibold">Κρατικά τέλη (περιβαλλοντικά)</h3>
        </div>
        Δεν εκδόθηκαν κρατικά τέλη στο διάστημα {formatDate(dateFrom)} – {formatDate(dateTo)}.
      </div>
    );
  }

  // Group daily entries: day → { 8: units, 16: units }
  const dailyMap = new Map<string, Record<number, { units: number; gross: number }>>();
  for (const d of data.daily) {
    if (!dailyMap.has(d.day)) dailyMap.set(d.day, {});
    dailyMap.get(d.day)![d.category] = { units: d.units, gross: d.gross_total };
  }
  const sortedDays = Array.from(dailyMap.keys()).sort((a, b) => b.localeCompare(a)).slice(0, 30);
  const maxDayUnits = Math.max(
    ...Array.from(dailyMap.values()).map(row =>
      Object.values(row).reduce((s, v) => s + v.units, 0)
    ),
    1
  );

  const totalUnits = data.totals.reduce((s, t) => s + t.units, 0);
  const totalNet = data.totals.reduce((s, t) => s + t.net_total, 0);
  const totalGross = data.totals.reduce((s, t) => s + t.gross_total, 0);

  return (
    <div className="bg-surface-2 dark:bg-surface-1/50 border border-line rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Leaf className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        <h3 className="font-semibold text-t1">Κρατικά τέλη (περιβαλλοντικά)</h3>
        <span className="text-xs text-t4 ml-2">
          {formatDate(data.range.from)} – {formatDate(data.range.to)}
        </span>
      </div>

      {/* Summary cards — totals per category */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4">
          <p className="text-xs text-t3 mb-1">Σύνολο τεμαχίων</p>
          <div className="text-2xl font-bold text-t1">{totalUnits}</div>
          <p className="text-[11px] text-t4 mt-0.5">
            {data.totals.length} {data.totals.length === 1 ? 'κατηγορία' : 'κατηγορίες'}
          </p>
        </div>

        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4">
          <p className="text-xs text-t3 mb-1">Σύνολο NET (στο κράτος)</p>
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{formatEuro(totalNet)}</div>
          <p className="text-[11px] text-t4 mt-0.5">χωρίς ΦΠΑ</p>
        </div>

        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4 sm:col-span-2">
          <p className="text-xs text-t3 mb-1">Σύνολο εισπράξεων από πελάτες</p>
          <div className="text-2xl font-bold text-t1">{formatEuro(totalGross)}</div>
          <p className="text-[11px] text-t4 mt-0.5">NET + ΦΠΑ 24%</p>
        </div>
      </div>

      {/* Per-category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {data.totals.map(t => {
          const style = CATEGORY_STYLE[t.category] || {
            color: 'text-t2', bg: 'bg-surface-2 dark:bg-surface-2/40', border: 'border-line-strong',
          };
          return (
            <div key={t.category} className={`${style.bg} border ${style.border} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <ShoppingBag className={`w-4 h-4 ${style.color}`} />
                <span className={`text-sm font-semibold ${style.color}`}>
                  Κωδ. {t.category} — {t.label}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <p className="text-[11px] text-t3">Τεμάχια</p>
                  <p className="text-lg font-bold text-t1">{t.units}</p>
                </div>
                <div>
                  <p className="text-[11px] text-t3">NET (κρατικό)</p>
                  <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatEuro(t.net_total)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-t3">Εισπραχθέν</p>
                  <p className="text-lg font-bold text-t1">{formatEuro(t.gross_total)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Daily timeline */}
      {sortedDays.length > 0 && (
        <div className="bg-surface-0/40 rounded-xl p-4 border border-line">
          <div className="flex items-center gap-2 mb-3 text-t2">
            <Calendar className="w-4 h-4" />
            <h4 className="text-sm font-semibold">Ημερήσια κατανομή (τελευταίες 30 ημέρες)</h4>
          </div>
          <div className="space-y-2">
            {sortedDays.map(day => {
              const row = dailyMap.get(day)!;
              const cat8 = row[8]?.units || 0;
              const cat16 = row[16]?.units || 0;
              const dayTotal = cat8 + cat16;
              const dayGross = (row[8]?.gross || 0) + (row[16]?.gross || 0);
              return (
                <div key={day}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-t2">{formatDate(day)}</span>
                    <span className="text-t3 flex-shrink-0 tabular-nums">
                      {dayTotal} τμχ · {formatEuro(dayGross)}
                    </span>
                  </div>
                  <div className="h-2 bg-surface-2 rounded-full overflow-hidden flex">
                    {cat8 > 0 && (
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${(cat8 / maxDayUnits) * 100}%` }}
                        title={`Σακούλες: ${cat8}`}
                      />
                    )}
                    {cat16 > 0 && (
                      <div
                        className="h-full bg-cyan-500"
                        style={{ width: `${(cat16 / maxDayUnits) * 100}%` }}
                        title={`Πλαστικά μιας χρήσης: ${cat16}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[11px] text-t3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-emerald-500" /> Κωδ. 8 — σακούλα
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-cyan-500" /> Κωδ. 16 — μιας χρήσης
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateFeesAnalytics;
